import { useIsClient } from "@uidotdev/usehooks";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCompanyLogoImage } from "../../../helpers/data_management";
import {
  bookmarkJobCompany,
  unBookmarkJobCompany,
} from "../../../redux/actions/company_action";
import { getJobs, updateSelectedJob } from "../../../redux/actions/job_action";
import {
  updateShowSignInModalStatus,
  updateSignUpOrigin,
} from "../../../redux/actions/navbar_action";
import { checkAuthentication } from "../../../redux/actions/user_action";
import {
  ArticleSimilarGrid,
  BookmarkButton,
  BoxContainer,
  ChipContainer,
  CompanyDetailContainer,
  CompanyLogo,
  CompanyName,
  JobBoostChip,
  JobCard,
  JobDetailChip,
  JobListContainer,
  JobTitleCard,
  LinkWrapper,
  SaveIcon,
  SimilarArticleWrapper,
  SimilarTitle,
  SparklesIconStyled,
  StyledDivider,
  UnsaveIcon,
  ViewOtherButton,
  ViewOtherButtonContainer,
} from "./styles";

function AdviceArticleJobs(props: any) {
  const { blog, SimilarArticles } = props || {};
  const dispatch: any = useDispatch();
  const router = useRouter();

  const currentArticleCategory =
    blog?.attributes?.articleCategories?.data?.[0]?.attributes?.category ?? "";

  const currentArticleSubCategory =
    blog?.attributes?.articleSubcategories?.data?.[0]?.attributes
      ?.subcategory ?? "";

  const isIntern = blog?.attributes?.slug?.toLowerCase()?.includes("intern");

  const [jobList, setJobList] = useState<any[]>([]);

  const jobsList = useSelector((state: any) => state?.jobs?.jobs || []);

  const redirectToJobSearch = () => {
    if (isIntern) {
      router.push("/internship");
    } else if (
      currentArticleCategory === "Career Pathways" &&
      currentArticleSubCategory
    ) {
      const slug = currentArticleSubCategory
        .trim()
        .toLowerCase()
        .replace(/&/g, "and")
        .replace(/\s+/g, "-")
        .replace(/[^\w-]+/g, "");

      router.push(slug ? `/${slug}-jobs` : "/jobs");
    } else {
      router.push("/jobs");
    }
  };

  useEffect(() => {
    if (!currentArticleCategory) return;

    const pagination = {
      first: null,
      last: null,
      startCursor: null,
      endCursor: null,
    };

    const fetchJobs = async () => {
      let combinedJobs: any[] = [];

      if (isIntern) {
        const internResult = await dispatch(
          getJobs(
            {
              keyword: "internship",
            },
            pagination,
            true,
            false
          )
        );

        combinedJobs = internResult?.payload || [];
      } else if (
        currentArticleCategory === "Career Pathways" &&
        currentArticleSubCategory
      ) {
        const keywordResult = await dispatch(
          getJobs(
            { keyword: currentArticleSubCategory },
            pagination,
            true,
            false
          )
        );
        const keywordJobs = keywordResult?.payload || [];

        if (keywordJobs?.length < 6) {
          const fallbackResult = await dispatch(
            getJobs({ keyword: "" }, pagination, true, false)
          );
          const fallbackJobs = fallbackResult?.payload || [];

          combinedJobs = [
            ...keywordJobs,
            ...fallbackJobs.filter(
              (job: any) =>
                !keywordJobs.some((kJob: any) => kJob?.id === job?.id)
            ),
          ];
        } else {
          combinedJobs = keywordJobs;
        }
      } else {
        const result = await dispatch(
          getJobs({ keyword: "" }, pagination, true, false)
        );
        combinedJobs = result?.payload || [];
      }

      setJobList(combinedJobs);
    };

    fetchJobs();
  }, [dispatch, currentArticleCategory, currentArticleSubCategory]);

  const JobCardComponent = ({ job, bookmarkState }: any) => {
    const isClient = useIsClient();
    const dispatch: any = useDispatch();
    const loggedIn = checkAuthentication();

    const [bookmark, setBookmark] = useState(job?.bookmark || false);
    const [bookmarkLoading, setBookmarkLoading] = useState(false);

    const showWorkExp = (job: any) => {
      return (
        (job?.minYearsExperience != null && job?.minYearsExperience > 0) ||
        (job?.maxYearsExperience != null && job?.maxYearsExperience > 0)
      );
    };

    const getWorkExp = (job: any) => {
      let exp =
        job?.minYearsExperience > 0 ? `${job?.minYearsExperience} to ` : "";
      exp += `${job?.maxYearsExperience} Years of Exp`;
      return exp;
    };

    const getCompanyLogo = (company: any) => getCompanyLogoImage(company);
    const bookmarkIcon = bookmark ? <UnsaveIcon /> : <SaveIcon />;

    const handleBookmarkJob = (e: any) => {
      e.stopPropagation();
      e.preventDefault();

      if (!loggedIn) {
        return dispatch(updateSelectedJob(job)).then(() => {
          dispatch(updateSignUpOrigin("save_job"));
          dispatch(updateShowSignInModalStatus(true));
        });
      }

      if (bookmarkLoading) return;

      setBookmarkLoading(true);

      const action = bookmark
        ? unBookmarkJobCompany({ jobId: job?.id })
        : bookmarkJobCompany({ jobId: job?.id });

      dispatch(action).then((res: any) => {
        if (res?.type?.includes("SUCCEED")) {
          setBookmark(!bookmark);
        }
        setBookmarkLoading(false);
      });
    };

    if (!job) return null;

    return (
      <LinkWrapper href={`/jobs/${job?.slug}`} target="_blank">
        <JobCard>
          <BoxContainer
            justifyContent="space-between"
            alignItems="center"
            marginBottom="10px"
          >
            <JobTitleCard>{job?.title}</JobTitleCard>
            {job?.boosted && (
              <JobBoostChip>
                <SparklesIconStyled />
                HOT
              </JobBoostChip>
            )}
          </BoxContainer>

          <ChipContainer>
            {job?.jobType && (
              <JobDetailChip type="jobType">{job?.jobType}</JobDetailChip>
            )}
            {job?.stateRegion && (
              <JobDetailChip type="state">{job?.stateRegion}</JobDetailChip>
            )}
            {showWorkExp(job) && (
              <JobDetailChip type="salary">{getWorkExp(job)}</JobDetailChip>
            )}
          </ChipContainer>

          <BoxContainer justifyContent="space-between" alignItems="center">
            <CompanyDetailContainer>
              <CompanyLogo src={getCompanyLogo(job?.company)} />
              <CompanyName>{job?.company?.name}</CompanyName>
            </CompanyDetailContainer>
            {loggedIn && (
              <BookmarkButton
                onClick={handleBookmarkJob}
                disabled={bookmarkState === undefined}
              >
                {bookmarkIcon}
              </BookmarkButton>
            )}
          </BoxContainer>
        </JobCard>
      </LinkWrapper>
    );
  };

  return jobsList?.length > 0 ? (
    <ArticleSimilarGrid>
      <StyledDivider />
      <SimilarArticleWrapper ref={SimilarArticles}>
        <SimilarTitle id="similar">Jobs You Might Like</SimilarTitle>
        <JobListContainer>
          {[...jobsList]
            .sort((a, b) => {
              // Prioritize boosted jobs
              if (a.boosted && !b.boosted) return -1;
              if (!a.boosted && b.boosted) return 1;
              return Math.random() - 0.5; // Random within same boost status
            })
            .slice(0, 6)
            .map((job: any) => (
              <JobCardComponent
                key={job?.id}
                job={job}
                bookmarkState={job?.bookmark}
              />
            ))}
        </JobListContainer>
      </SimilarArticleWrapper>

      <ViewOtherButtonContainer>
        <ViewOtherButton onClick={redirectToJobSearch}>
          See More Jobs Like This
        </ViewOtherButton>
      </ViewOtherButtonContainer>
    </ArticleSimilarGrid>
  ) : null;
}

export default AdviceArticleJobs;
